import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { JournalPatientConfirmDialog } from '@/components/journal/JournalPatientConfirmDialog';
import { fetchClinicRoles, clinicRolesQueryKey, type ClinicRoleRow } from '@/hooks/useClinicRoles';
import { useAuth } from '@/lib/auth';
import { patientDisplayName } from '@/lib/patientQueryCache';
import { supabase } from '@/lib/supabase';
import {
  journalPatientConfirmMembershipQueryKey,
  journalPatientConfirmState,
  type JournalPatientConfirmMember,
} from '@/lib/journalPatientConfirm';

async function fetchJournalPatientConfirmMember(
  clinicId: string,
  userId: string,
): Promise<JournalPatientConfirmMember | null> {
  const { data, error } = await supabase
    .from('clinic_memberships')
    .select('role, custom_role_id, confirm_journal_patient')
    .eq('user_id', userId)
    .eq('clinic_id', clinicId)
    .eq('status', 'active')
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;
  return {
    role: data.role,
    custom_role_id: data.custom_role_id,
    confirm_journal_patient: Boolean(data.confirm_journal_patient),
  };
}

/**
 * Asks before a journal is finished when the signed-in member's effective
 * setting is on (role forces it, or their own preference is on).
 * If the setting cannot be read, finish continues so a missing migration
 * does not block journals.
 */
export function useJournalFinishConfirmation(input: {
  clinicId: string;
  patientId: string;
  patientName?: string | null;
}): {
  confirmFinish: () => Promise<boolean>;
  pending: boolean;
  dialog: ReactNode;
} {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const userId = user?.id ?? null;
  const [open, setOpen] = useState(false);
  const [shownName, setShownName] = useState('');
  const [pending, setPending] = useState(false);
  const resolver = useRef<((ok: boolean) => void) | null>(null);
  const gate = useRef<Promise<boolean> | null>(null);

  useEffect(() => {
    return () => {
      resolver.current?.(false);
      resolver.current = null;
    };
  }, []);

  const settle = useCallback((ok: boolean) => {
    setOpen(false);
    const resolve = resolver.current;
    resolver.current = null;
    resolve?.(ok);
  }, []);

  const confirmFinish = useCallback(async () => {
    if (gate.current) {
      await gate.current;
      return false;
    }

    const run = (async () => {
      setPending(true);
      try {
        if (!input.clinicId || !userId) return true;
        let member: JournalPatientConfirmMember | null;
        let roles: ClinicRoleRow[];
        try {
          [member, roles] = await Promise.all([
            queryClient.fetchQuery({
              queryKey: journalPatientConfirmMembershipQueryKey(input.clinicId, userId),
              queryFn: () => fetchJournalPatientConfirmMember(input.clinicId, userId),
              staleTime: 0,
            }),
            queryClient.fetchQuery({
              queryKey: clinicRolesQueryKey(input.clinicId),
              queryFn: () => fetchClinicRoles(input.clinicId),
              staleTime: 0,
            }),
          ]);
        } catch {
          return true;
        }
        if (!member || !journalPatientConfirmState(member, roles).effective) return true;

        let name = input.patientName?.trim() || '';
        if (!name && input.patientId) {
          try {
            const { data, error } = await supabase
              .from('patients')
              .select('first_name, last_name')
              .eq('id', input.patientId)
              .maybeSingle();
            if (!error && data) name = patientDisplayName(data);
          } catch {
            name = '';
          }
        }
        setShownName(name || '—');
        setOpen(true);
        return await new Promise<boolean>((resolve) => {
          resolver.current = resolve;
        });
      } finally {
        setPending(false);
      }
    })();

    gate.current = run;
    try {
      return await run;
    } finally {
      gate.current = null;
    }
  }, [input.clinicId, input.patientId, input.patientName, queryClient, userId]);

  const dialog = (
    <JournalPatientConfirmDialog
      open={open}
      patientName={shownName}
      onConfirm={() => settle(true)}
      onCancel={() => settle(false)}
    />
  );

  return { confirmFinish, pending, dialog };
}
